import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowRight, Compass, Mountain, Box, Grid3x3, Calendar, ClipboardList, FileText,
  Cloud, Shield, Users, Zap, Cpu, HardDrive, Server, Database, Radio, UserCheck,
  ClipboardCheck, ShieldCheck, Layers, Sliders, Lock, Rocket, Gauge, Mail, Phone,
  Linkedin, Youtube, MessageCircle, Check,
} from "lucide-react";
import SminHeader from "@/components/SminHeader";
import SminFooter from "@/components/SminFooter";
import { MARKETING_NAV as NAV } from "@/lib/marketingNav";

const WORKFLOW = [
  { label: "Exploration",        Icon: Compass },
  { label: "Geology",            Icon: Mountain },
  { label: "Modelling",          Icon: Box },
  { label: "Resource Estimation", Icon: Grid3x3 },
  { label: "Mine Planning",      Icon: Calendar },
  { label: "Scheduling",         Icon: ClipboardList },
  { label: "Reporting",          Icon: FileText },
];

const STATS = [
  ["100M+",     "Drillhole Intervals"],
  ["500M+",     "Assay Records"],
  ["10M+",      "Block Model Cells"],
  ["Unlimited", "Projects"],
  ["Unlimited", "Companies"],
  ["95+",       "Performance Score"],
];

const FEATURES = [
  { Icon: HardDrive, title: "Geological Data",       body: "Collar, Survey, Assay, Lithology, Density, Structure & More" },
  { Icon: Box,       title: "3D Geological Modelling", body: "Wireframes, Surfaces, Domains, Faults, Sections & Volumes" },
  { Icon: Grid3x3,   title: "Resource Estimation",   body: "Variography, Kriging, Block Models, Classification, Confidence Analysis" },
  { Icon: Mountain,  title: "Mine Planning",         body: "Pit Optimisation, Pushbacks, Bench Design, Waste Dumps, Underground Design" },
  { Icon: Calendar,  title: "Scheduling",            body: "Production Scheduling, NPV Optimisation, Cash Flow, Scenario Planning" },
  { Icon: FileText,  title: "Technical Reporting",   body: "CRIRSCO · SAMREC · PERC · SME · S-K 1300 · KAZRC — Professional PDF, Word, Excel & PPT reports across every major international reporting code." },
];

const BENEFITS = [
  { Icon: Cloud,  title: "Browser Based",   body: "No installation. Access anywhere, anytime." },
  { Icon: Shield, title: "Enterprise Ready", body: "Secure, scalable and built for large mining operations." },
  { Icon: Gauge,  title: "High Performance", body: "Fast processing and GPU powered 3D visualisation." },
];

const TECH = [
  { Icon: Cpu,           label: "AI Assistant" },
  { Icon: Layers,        label: "GPU Rendering" },
  { Icon: Cloud,         label: "Cloud Platform" },
  { Icon: Database,      label: "MongoDB" },
  { Icon: Radio,         label: "REST API" },
  { Icon: UserCheck,     label: "Role Based Access" },
  { Icon: ClipboardCheck, label: "Audit Logs" },
  { Icon: ShieldCheck,   label: "Secure & Reliable" },
];

const BADGES = [
  { Icon: Lock,    top: "Secure",     bot: "Cloud Platform" },
  { Icon: Shield,  top: "Enterprise", bot: "Grade Security" },
  { Icon: Users,   top: "Trusted by", bot: "Mining Experts" },
  { Icon: Rocket,  top: "Built for",  bot: "Performance" },
];

const HERO_3D = "https://images.unsplash.com/photo-1615960403161-71a488ac71b5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwyfHwzZCUyMG1pbmluZyUyMHZpc3VhbGl6YXRpb258ZW58MHx8fHwxNzM4MjEzOTAxfDA&ixlib=rb-4.1.0&q=85";

function Section({ id, className = "", children }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current) return;
    const el = ref.current;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add("smin-visible");
          io.disconnect();
        }
      },
      { threshold: 0.15 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return (
    <section id={id} ref={ref} className={"w-full smin-observe " + className}>
      <div className="max-w-7xl mx-auto px-6 md:px-10">{children}</div>
    </section>
  );
}

/** useCountUp — animates a value from 0 to target over `duration` ms. */
function useCountUp(target, duration = 1400, decimals = 1) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    let raf; const start = performance.now();
    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      setValue(target * eased);
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);
  return decimals === 0 ? Math.round(value) : value.toFixed(decimals);
}

function HeroMockup() {
  const ore   = useCountUp(14.2, 2400, 1);
  const grade = useCountUp(1.42, 2400, 2);
  const npv   = useCountUp(428,  2800, 0);

  // Isometric block model config. Each cell has a deterministic grade
  // (0-100) and an appearance delay so the whole block "kriges" in from
  // the centre outward.
  const GRID_W = 9;      // cells along X
  const GRID_D = 5;      // cells along Y (depth into page)
  const CELL_W = 14;     // isometric tile width  (px)
  const CELL_H = 7;      // isometric tile height (px)
  const ORIGIN_X = 200;
  const ORIGIN_Y = 165;

  const cells = [];
  for (let dy = 0; dy < GRID_D; dy++) {
    for (let dx = 0; dx < GRID_W; dx++) {
      const cx = ORIGIN_X + (dx - dy) * CELL_W;
      const cy = ORIGIN_Y + (dx + dy) * CELL_H;
      // Radial grade: hot in the centre (~7,3), cold at the edges
      const rd = Math.hypot(dx - GRID_W / 2 + 0.5, dy - GRID_D / 2 + 0.5);
      const gradeVal = Math.max(0, 1 - rd / 4);      // 0..1
      // Manhattan distance from centre → animation stagger
      const stagger = Math.abs(dx - Math.floor(GRID_W / 2))
                    + Math.abs(dy - Math.floor(GRID_D / 2));
      cells.push({ dx, dy, cx, cy, gradeVal, stagger });
    }
  }

  // Six drill collars evenly spaced above the block model.
  const drills = Array.from({ length: 6 }, (_, i) => ({
    x: 110 + i * 30,
    startY: 40,
    endY: 140 + (i % 2) * 18,
    delay: 0.15 * i,
  }));

  // Rolling data ticker — cycles through 5 events every ~4 s
  const [tickIdx, setTickIdx] = React.useState(0);
  React.useEffect(() => {
    const t = setInterval(() => setTickIdx((i) => (i + 1) % 5), 2600);
    return () => clearInterval(t);
  }, []);
  const tickerEvents = [
    { label: "IMPORTING", value: "DDH-127", detail: "asy · 312 m" },
    { label: "KRIGING",   value: "Bench 1180", detail: "6,432 cells" },
    { label: "GRADE",     value: "2.34 g/t Au", detail: "measured" },
    { label: "OPT. PIT",  value: "$428.4 M",   detail: "LG shell" },
    { label: "SCHEDULE",  value: "Y3 · Q2",    detail: "16.8 Mt" },
  ];

  return (
    <div
      className="relative rounded-xl overflow-hidden aspect-[4/3] border border-neutral-200 shadow-2xl smin-fade-in"
      style={{ background: "linear-gradient(135deg, #F7FBF6 0%, #E8F5E9 100%)" }}
      data-testid="hero-mockup"
    >
      {/* window chrome */}
      <div className="absolute top-0 left-0 right-0 h-8 bg-white/80 backdrop-blur border-b border-neutral-200 flex items-center gap-1.5 px-3 z-10">
        <span className="h-2.5 w-2.5 rounded-full bg-red-400" />
        <span className="h-2.5 w-2.5 rounded-full bg-yellow-400" />
        <span className="h-2.5 w-2.5 rounded-full" style={{ background: "#4CAF50" }} />
        <span className="ml-4 text-[10px] font-mono uppercase tracking-widest text-neutral-500">
          smin · resource modelling · <span style={{ color: "#4CAF50" }}>LIVE</span>
          <span className="smin-cursor-blink" style={{ color: "#4CAF50" }}>▍</span>
        </span>
      </div>

      <div className="absolute inset-0 top-8 p-4 grid grid-cols-3 grid-rows-3 gap-3">
        {/* KPI cards */}
        <div className="bg-white rounded-lg p-3 shadow-sm border border-neutral-100 smin-fade-in">
          <div className="text-[9px] font-mono uppercase tracking-widest text-neutral-500">Ore Tonnes</div>
          <div className="mt-1 text-lg font-bold text-neutral-900 tabular-nums">{ore} Mt</div>
          <div className="text-[10px]" style={{ color: "#4CAF50" }}>▲ 4.3%</div>
        </div>
        <div className="bg-white rounded-lg p-3 shadow-sm border border-neutral-100 smin-fade-in" style={{ animationDelay: "100ms" }}>
          <div className="text-[9px] font-mono uppercase tracking-widest text-neutral-500">Avg Grade</div>
          <div className="mt-1 text-lg font-bold text-neutral-900 tabular-nums">{grade} g/t</div>
          <div className="text-[10px]" style={{ color: "#4CAF50" }}>Au</div>
        </div>
        <div className="bg-white rounded-lg p-3 shadow-sm border border-neutral-100 smin-fade-in" style={{ animationDelay: "200ms" }}>
          <div className="text-[9px] font-mono uppercase tracking-widest text-neutral-500">NPV</div>
          <div className="mt-1 text-lg font-bold text-neutral-900 tabular-nums">${npv} M</div>
          <div className="text-[10px]" style={{ color: "#4CAF50" }}>▲ LG opt.</div>
        </div>

        {/* Main scene — isometric block model + drilling */}
        <div className="col-span-2 row-span-2 bg-white rounded-lg shadow-sm border border-neutral-100 relative overflow-hidden smin-fade-in-delayed">
          <svg viewBox="0 0 400 260" className="w-full h-full" preserveAspectRatio="xMidYMid meet">
            <defs>
              {/* Grade colour ramp — cold pale green → hot deep green */}
              <linearGradient id="hotOre" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%"   stopColor="#4CAF50" />
                <stop offset="100%" stopColor="#1B5E20" />
              </linearGradient>
              <linearGradient id="coolOre" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%"   stopColor="#C8E6C9" />
                <stop offset="100%" stopColor="#81C784" />
              </linearGradient>
              <filter id="softGlow" x="-40%" y="-40%" width="180%" height="180%">
                <feGaussianBlur stdDeviation="1.4"/>
              </filter>
            </defs>

            {/* Compass */}
            <g transform="translate(28, 30)">
              <circle r={14} fill="white" stroke="#4CAF50" strokeWidth={1.5} />
              <path d="M0,-8 L4,6 L0,3 L-4,6 Z" fill="#4CAF50" />
              <text y={22} textAnchor="middle" fontSize="8" fontFamily="Inter" fill="#4CAF50" fontWeight="700">N</text>
            </g>

            {/* Iso ground grid — subtle */}
            <g opacity={0.25} stroke="#4CAF50" strokeWidth={0.4}>
              {Array.from({ length: 11 }).map((_, i) => {
                const t = i - 5;
                return (
                  <g key={i}>
                    <line x1={ORIGIN_X + t * CELL_W} y1={ORIGIN_Y + t * CELL_H}
                          x2={ORIGIN_X + t * CELL_W - GRID_D * CELL_W}
                          y2={ORIGIN_Y + t * CELL_H + GRID_D * CELL_H}/>
                    <line x1={ORIGIN_X - t * CELL_W} y1={ORIGIN_Y + t * CELL_H}
                          x2={ORIGIN_X - t * CELL_W + GRID_W * CELL_W}
                          y2={ORIGIN_Y + t * CELL_H + GRID_W * CELL_H}/>
                  </g>
                );
              })}
            </g>

            {/* Drill holes — descend, then leave a chip trail behind */}
            {drills.map((d, i) => (
              <g key={i} className="smin-drill" style={{ animationDelay: `${d.delay}s` }}>
                <line x1={d.x} y1={d.startY} x2={d.x} y2={d.endY}
                      stroke="#2E7D32" strokeWidth={0.8}
                      strokeDasharray="2 2" opacity={0.7}/>
                {/* Assay chips along the hole */}
                {[0.35, 0.55, 0.75, 0.9].map((f, ci) => {
                  const y = d.startY + (d.endY - d.startY) * f;
                  return (
                    <circle key={ci} cx={d.x} cy={y} r={2.2}
                            fill={ci % 2 ? "url(#hotOre)" : "url(#coolOre)"}
                            className="smin-assay-chip"
                            style={{ animationDelay: `${d.delay + 0.8 + ci * 0.12}s` }}/>
                  );
                })}
                {/* Drill bit tip (bounces once at bottom) */}
                <circle cx={d.x} cy={d.endY} r={2.6} fill="#1B5E20"
                        className="smin-drill-bit"
                        style={{ animationDelay: `${d.delay + 0.6}s` }}
                        filter="url(#softGlow)"/>
              </g>
            ))}

            {/* Isometric block model — grade cells materialise from centre out */}
            <g>
              {cells.map((c, i) => {
                const isHot = c.gradeVal > 0.5;
                return (
                  <g key={i} className="smin-block-cell"
                     style={{ animationDelay: `${1.4 + c.stagger * 0.06}s`,
                              transformOrigin: `${c.cx}px ${c.cy}px` }}>
                    {/* Top face (rhombus) */}
                    <polygon
                      points={`
                        ${c.cx},${c.cy}
                        ${c.cx + CELL_W},${c.cy + CELL_H}
                        ${c.cx},${c.cy + CELL_H * 2}
                        ${c.cx - CELL_W},${c.cy + CELL_H}`}
                      fill={isHot ? "url(#hotOre)" : "url(#coolOre)"}
                      stroke="#1B5E20" strokeWidth={0.3}
                      opacity={0.55 + c.gradeVal * 0.45}/>
                    {/* Left face (parallelogram, deeper) */}
                    <polygon
                      points={`
                        ${c.cx - CELL_W},${c.cy + CELL_H}
                        ${c.cx},${c.cy + CELL_H * 2}
                        ${c.cx},${c.cy + CELL_H * 2 + 5}
                        ${c.cx - CELL_W},${c.cy + CELL_H + 5}`}
                      fill={isHot ? "#1B5E20" : "#66BB6A"}
                      opacity={0.7}/>
                    {/* Right face */}
                    <polygon
                      points={`
                        ${c.cx + CELL_W},${c.cy + CELL_H}
                        ${c.cx},${c.cy + CELL_H * 2}
                        ${c.cx},${c.cy + CELL_H * 2 + 5}
                        ${c.cx + CELL_W},${c.cy + CELL_H + 5}`}
                      fill={isHot ? "#2E7D32" : "#81C784"}
                      opacity={0.8}/>
                  </g>
                );
              })}
            </g>

            {/* Sweeping "kriging" scan line — slowly crosses the block model */}
            <rect x={0} y={155} width={4} height={80}
                  fill="#4CAF50" opacity={0.35}
                  className="smin-scan-sweep" filter="url(#softGlow)"/>

            {/* Scale bar */}
            <g transform="translate(320, 240)">
              <line x1={0} y1={0} x2={50} y2={0} stroke="#4CAF50" strokeWidth={1.5} />
              <text x={25} y={-4} textAnchor="middle" fontSize="7" fontFamily="Inter" fill="#4CAF50">500 m</text>
            </g>
          </svg>

          {/* Data ticker overlay — bottom bar */}
          <div className="absolute bottom-0 left-0 right-0 h-6 bg-neutral-900/90 flex items-center px-3 gap-3 overflow-hidden">
            <span className="text-[8.5px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded-sm bg-[#4CAF50] text-white">
              {tickerEvents[tickIdx].label}
            </span>
            <span className="text-[10.5px] font-mono text-white tabular-nums smin-tick-fade" key={tickIdx}>
              {tickerEvents[tickIdx].value}
            </span>
            <span className="text-[9.5px] font-mono text-neutral-400 smin-tick-fade" key={`d-${tickIdx}`}>
              — {tickerEvents[tickIdx].detail}
            </span>
            <span className="ml-auto text-[8.5px] font-mono text-[#4CAF50] flex items-center gap-1.5">
              <span className="h-1.5 w-1.5 rounded-full bg-[#4CAF50] smin-live-pulse"/>
              LIVE
            </span>
          </div>
        </div>

        {/* Grade × Tonnage sparkline */}
        <div className="bg-white rounded-lg p-3 shadow-sm border border-neutral-100 flex flex-col smin-fade-in-delayed" style={{ animationDelay: "300ms" }}>
          <div className="text-[9px] font-mono uppercase tracking-widest text-neutral-500">Grade × Tonnage</div>
          <svg viewBox="0 0 100 60" className="w-full flex-1 mt-1">
            <polyline
              className="smin-spark-line"
              points="0,50 12,42 24,36 36,28 48,22 60,16 72,13 84,10 100,8"
              fill="none" stroke="#4CAF50" strokeWidth={2}
              pathLength={400}
            />
            <polyline
              className="smin-spark-fill"
              points="0,50 12,42 24,36 36,28 48,22 60,16 72,13 84,10 100,8 100,60 0,60"
              fill="#4CAF50"
            />
          </svg>
        </div>

        {/* Classification breakdown — bars now animate width */}
        <div className="bg-white rounded-lg p-3 shadow-sm border border-neutral-100 smin-fade-in-delayed" style={{ animationDelay: "400ms" }}>
          <div className="text-[9px] font-mono uppercase tracking-widest text-neutral-500">Classification</div>
          <div className="mt-2 space-y-2">
            {[
              { label: "Measured", pct: 42, color: "#4CAF50" },
              { label: "Indicated", pct: 35, color: "#81C784" },
              { label: "Inferred",  pct: 23, color: "#C8E6C9" },
            ].map((r) => (
              <div key={r.label}>
                <div className="flex items-center justify-between text-[9.5px] mb-0.5">
                  <span className="text-neutral-700">{r.label}</span>
                  <span className="font-mono text-neutral-600 tabular-nums">{r.pct}%</span>
                </div>
                <div className="h-1 rounded-full bg-neutral-100 overflow-hidden">
                  <div className="h-full rounded-full smin-class-bar"
                       style={{ width: `${r.pct}%`, background: r.color,
                                animationDelay: `${0.6 + r.pct * 0.005}s` }}/>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function LandingPage() {
  const nav = useNavigate();
  const requestDemo = () => nav("/signup");

  return (
    <div className="min-h-screen flex flex-col bg-white text-neutral-900">
      <SminHeader
        variant="marketing"
        forceGuest
        navLinks={NAV}
      />

      {/* ═══════════ HERO ═══════════ */}
      <Section className="py-16 md:py-20">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-neutral-900 leading-[1.05]">
              Modern Mine<br/>Planning Software
            </h1>
            <h2 className="mt-3 text-3xl md:text-4xl font-bold tracking-tight leading-tight" style={{ color: "#4CAF50" }}>
              The Intelligence<br/>Beneath the Surface.
            </h2>
            <p className="mt-5 text-sm md:text-base text-neutral-600 leading-relaxed max-w-lg">
              SMIN is a cloud-based mine planning platform that helps geologists and
              mine engineers manage data, build 3D models, estimate resources,
              optimise mine plans, schedule production and generate professional
              reports — all in one integrated platform.
            </p>
            <div className="mt-7 flex flex-wrap items-center gap-3">
              <button
                onClick={requestDemo}
                data-testid="hero-request-demo"
                className="inline-flex items-center gap-2 h-11 px-5 rounded-md text-white font-semibold text-sm"
                style={{ background: "#4CAF50" }}
              >
                Request Demo <ArrowRight className="h-4 w-4" />
              </button>
              <a
                href="mailto:info@sresources.co"
                data-testid="hero-contact-sales"
                className="inline-flex items-center gap-2 h-11 px-5 rounded-md border-2 font-semibold text-sm text-neutral-900 hover:bg-neutral-50 transition"
                style={{ borderColor: "#4CAF50" }}
              >
                Contact Sales <ArrowRight className="h-4 w-4" />
              </a>
            </div>
            <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
              {BADGES.map(({ Icon, top, bot }) => (
                <div key={top} className="flex items-center gap-2.5">
                  <span className="inline-flex items-center justify-center h-9 w-9 rounded-full bg-neutral-100">
                    <Icon className="h-4 w-4 text-neutral-700" strokeWidth={2} />
                  </span>
                  <div className="leading-tight">
                    <div className="text-xs font-semibold text-neutral-900">{top}</div>
                    <div className="text-[11px] text-neutral-500">{bot}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="relative">
            <HeroMockup />
          </div>
        </div>
      </Section>

      {/* ═══════════ WORKFLOW STRIP ═══════════ */}
      <Section id="workflow" className="py-14 border-t border-neutral-100">
        <h2 className="text-center text-2xl md:text-3xl font-bold tracking-tight text-neutral-900">
          One Platform. Complete Mining Workflow.
        </h2>
        <div className="mt-10 flex flex-wrap items-start justify-center gap-x-2 gap-y-6">
          {WORKFLOW.map(({ label, Icon }, i) => (
            <React.Fragment key={label}>
              <div className="flex flex-col items-center w-24 text-center">
                <span className="inline-flex items-center justify-center h-14 w-14 rounded-full border-2" style={{ borderColor: "#4CAF50" }}>
                  <Icon className="h-6 w-6" style={{ color: "#4CAF50" }} strokeWidth={2} />
                </span>
                <span className="mt-3 text-xs text-neutral-700 font-medium leading-tight">{label}</span>
              </div>
              {i < WORKFLOW.length - 1 && (
                <ArrowRight className="h-4 w-4 text-neutral-300 mt-5 shrink-0" />
              )}
            </React.Fragment>
          ))}
        </div>
      </Section>

      {/* ═══════════ STATS BAR (BLACK) ═══════════ */}
      <div className="bg-[#111] py-8">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-2 md:grid-cols-6 gap-6">
          {STATS.map(([n, l]) => (
            <div key={l} className="text-center">
              <div className="text-2xl md:text-3xl font-bold" style={{ color: "#4CAF50" }}>{n}</div>
              <div className="text-[11px] uppercase tracking-widest text-white/80 mt-1">{l}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ═══════════ FEATURES GRID ═══════════ */}
      <Section id="features" className="py-16 md:py-20">
        <div className="text-center">
          <h2 className="text-2xl md:text-3xl font-bold tracking-tight text-neutral-900">
            Everything You Need<br className="hidden md:block" />
            to Plan, Optimise and Report.
          </h2>
          <div className="mt-2 mx-auto h-1 w-20 rounded-full" style={{ background: "#4CAF50" }} />
        </div>
        <div className="mt-10 grid md:grid-cols-3 gap-5">
          {FEATURES.map(({ Icon, title, body }) => (
            <div key={title}
                 data-testid={`feature-${title.toLowerCase().replace(/\s+/g, "-")}`}
                 className="p-6 rounded-xl border border-neutral-200 bg-white hover:shadow-md transition">
              <span className="inline-flex items-center justify-center h-10 w-10 rounded-lg" style={{ background: "#4CAF5015" }}>
                <Icon className="h-5 w-5" style={{ color: "#4CAF50" }} strokeWidth={2} />
              </span>
              <h3 className="mt-4 text-base font-bold text-neutral-900">{title}</h3>
              <p className="mt-1.5 text-sm text-neutral-600 leading-relaxed">{body}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* ═══════════ WHY SMIN (LIGHT GREY) ═══════════ */}
      <div className="bg-neutral-50 py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="text-xs font-semibold uppercase tracking-widest" style={{ color: "#4CAF50" }}>Why SMIN</div>
            <h2 className="mt-3 text-3xl md:text-4xl font-bold tracking-tight text-neutral-900">
              Built for Modern<br />Mining Companies.
            </h2>
            <p className="mt-4 text-sm text-neutral-600 max-w-md">
              Enterprise-ready platform that delivers performance, security and reliability at every scale.
            </p>
            <button
              onClick={requestDemo}
              className="mt-6 inline-flex items-center gap-2 h-10 px-5 rounded-md text-white font-semibold text-sm"
              style={{ background: "#4CAF50" }}
            >
              Learn More <ArrowRight className="h-4 w-4" />
            </button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 text-center">
            {BENEFITS.map(({ Icon, title, body }) => (
              <div key={title} className="flex flex-col items-center">
                <span className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-white shadow-sm border border-neutral-200">
                  <Icon className="h-7 w-7" style={{ color: "#4CAF50" }} strokeWidth={1.75} />
                </span>
                <h3 className="mt-4 text-base font-bold text-neutral-900">{title}</h3>
                <p className="mt-1.5 text-sm text-neutral-600 leading-relaxed max-w-[16rem]">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ═══════════ TECHNOLOGY STRIP ═══════════ */}
      <Section className="py-14">
        <h2 className="text-center text-2xl md:text-3xl font-bold tracking-tight text-neutral-900">
          Powerful Technology. Built for Performance.
        </h2>
        <div className="mt-10 grid grid-cols-4 md:grid-cols-8 gap-4">
          {TECH.map(({ Icon, label }) => (
            <div key={label} className="p-4 rounded-lg border border-neutral-200 bg-white flex flex-col items-center text-center">
              <Icon className="h-6 w-6" style={{ color: "#4CAF50" }} strokeWidth={1.75} />
              <span className="mt-2 text-[11px] font-medium text-neutral-700 leading-tight">{label}</span>
            </div>
          ))}
        </div>
      </Section>

      {/* ═══════════ CTA (GREEN) ═══════════ */}
      <Section className="py-8">
        <div
          className="rounded-2xl px-8 md:px-12 py-10 flex flex-col md:flex-row items-center justify-between gap-6"
          style={{ background: "linear-gradient(135deg, #4CAF50 0%, #3d9142 100%)" }}
        >
          <div className="flex items-center gap-5 text-white">
            <span
              className="inline-flex items-center bg-white rounded-lg px-4 py-2 shrink-0"
              data-testid="landing-cta-logo"
            >
              <img
                src="/branding/smin-logo-horizontal.png?v=9"
                alt="SMIN"
                className="h-10 w-auto object-contain"
              />
            </span>
            <div>
              <h2 className="text-2xl md:text-3xl font-bold tracking-tight">
                Ready to Transform Your<br />Mine Planning Workflow?
              </h2>
              <p className="mt-1 text-sm text-white/90">
                Book a demo and see how SMIN can help you plan smarter, optimise better and report faster.
              </p>
            </div>
          </div>
          <div className="flex flex-col gap-3 shrink-0 w-full md:w-auto">
            <button onClick={requestDemo}
              className="inline-flex items-center justify-center gap-2 h-11 min-w-[200px] px-6 rounded-md bg-white font-semibold text-sm text-neutral-900 hover:bg-neutral-100 transition">
              Request Demo <ArrowRight className="h-4 w-4" />
            </button>
            <a href="mailto:info@sresources.co"
              className="inline-flex items-center justify-center gap-2 h-11 min-w-[200px] px-6 rounded-md border-2 border-white font-semibold text-sm text-white hover:bg-white/10 transition">
              Contact Sales <ArrowRight className="h-4 w-4" />
            </a>
          </div>
        </div>
      </Section>

      {/* ═══════════ FOOTER ═══════════ */}
      <SminFooter />
    </div>
  );
}
